// Super Admin -> Kiri: master on/off, per-action toggles, usage analytics.
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bird, Power, Activity, Sparkles, Users, ListChecks } from "lucide-react";
import { toast } from "sonner";

const Toggle = ({ on, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`w-10 h-5 rounded-full relative transition-colors shrink-0 ${on ? "bg-[#FF5F15]" : "bg-zinc-300"}`}
  >
    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${on ? "left-5" : "left-0.5"}`} />
  </button>
);

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="border border-zinc-200 bg-white p-4" data-testid={`kiri-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
      <Icon className="w-3 h-3" /> {label}
    </div>
    <div className="text-2xl font-display font-black">{value}</div>
  </div>
);

export function KiriControlPanel() {
  const [settings, setSettings] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/platform/kiri-settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/platform/kiri-analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const patch = async (body) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/platform/kiri-settings", body);
      setSettings((s) => ({ ...s, ...data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  if (!settings) return <div className="text-xs text-zinc-500 font-mono">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="kiri-control-panel">
      <div className="border-2 border-zinc-900 bg-white p-5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#09090B] flex items-center justify-center">
            <Bird className="w-5 h-5 text-[#FF5F15]" />
          </div>
          <div>
            <div className="font-display font-black uppercase tracking-tight text-lg">Kiri — Master switch</div>
            <p className="text-xs text-zinc-500">Turns the assistant off for every organisation, everywhere.</p>
          </div>
        </div>
        <Toggle on={settings.kiri_enabled} onClick={() => patch({ kiri_enabled: !settings.kiri_enabled })} testId="kiri-master-toggle" />
      </div>

      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Activity} label="Requests" value={analytics.total_requests} />
          <StatCard icon={ListChecks} label="Actions" value={analytics.total_actions} />
          <StatCard icon={Sparkles} label="Drafts created" value={analytics.total_drafts_created} />
          <StatCard icon={Users} label="Active users" value={analytics.active_users} />
        </div>
      )}

      <div className="border border-zinc-200 bg-white">
        <div className="px-4 py-3 border-b border-zinc-200 text-xs font-bold uppercase tracking-wider text-zinc-700">
          Per-action controls
        </div>
        <div className="divide-y divide-zinc-100">
          {Object.entries(settings.actions_meta || {}).map(([aid, meta]) => (
            <div key={aid} className="flex items-center justify-between gap-3 px-4 py-2.5" data-testid={`kiri-action-row-${aid}`}>
              <div>
                <div className="text-sm font-medium">{meta.label}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{aid} · risk: {meta.risk}</div>
              </div>
              <Toggle
                on={!!settings.kiri_actions?.[aid]}
                onClick={() => patch({ kiri_actions: { [aid]: !settings.kiri_actions?.[aid] } })}
                testId={`kiri-action-toggle-${aid}`}
              />
            </div>
          ))}
        </div>
      </div>

      {analytics && analytics.top_actions?.length > 0 && (
        <div className="border border-zinc-200 bg-white">
          <div className="px-4 py-3 border-b border-zinc-200 text-xs font-bold uppercase tracking-wider text-zinc-700">
            Top actions
          </div>
          <div className="divide-y divide-zinc-100">
            {analytics.top_actions.map((a) => (
              <div key={a.action_id} className="flex items-center justify-between px-4 py-2 text-sm">
                <span>{a.action_id}</span>
                <span className="font-mono text-zinc-500">{a.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Button variant="outline" size="sm" onClick={load} disabled={busy} className="rounded-sm" data-testid="kiri-refresh-btn">
        Refresh
      </Button>
    </div>
  );
}
